/**
 * Admin Deal Flow API Service
 * Handles all admin deal management, distributions, contacts, communications
 */
import api from './api';
import { TENANT } from '../config';

const BASE = '/admin/deal-flow';

/**
 * Where the e-signature endpoints live on this tenant.
 *
 * The signing engine is tenant-neutral, but every route that drives it sat
 * behind `capitalMarketsEnabled` — which is `tenant.id !== 'emerge'`. So on
 * eMerge `/admin/deal-flow` is not mounted at all, and this page was calling
 * endpoints that answered 404 while documents were being sent and signed
 * perfectly well underneath. The screen showing nothing read as "it did not
 * work".
 *
 * Only the e-signature calls move. Deals, distributions and contacts stay on
 * the deal-flow base because they are genuinely capital-markets features and
 * genuinely absent here.
 */
const ESIGN_BASE = TENANT.id === 'emerge' ? '/admin/policies' : BASE;

// Helper to type API responses as any to avoid TS18046 with .data access
const get = (url: string, opts?: any): Promise<any> => api.get(url, opts);
const post = (url: string, data?: any): Promise<any> => api.post(url, data);
const put = (url: string, data?: any): Promise<any> => api.put(url, data);

// ============================================================
// Dashboard
// ============================================================
export const getAdminDealStats = () => get(`${BASE}/stats`);

// ============================================================
// Deals / Capital Raises
// ============================================================
export const getDeals = (params?: { status?: string; type?: string; page?: number }) =>
  get(`${BASE}/deals`, { params });

export const getDeal = (id: string) => get(`${BASE}/deals/${id}`);

export const createDeal = (data: {
  title: string;
  type?: string;
  summary?: string;
  raise_target?: number;
  min_investment?: number;
  status?: string;
  settings?: Record<string, any>;
}) => post(`${BASE}/deals`, data);

export const updateDeal = (id: string, data: Partial<{
  title: string;
  type: string;
  summary: string;
  status: string;
  raise_target: number;
  min_investment: number;
  settings: Record<string, any>;
}>) => put(`${BASE}/deals/${id}`, data);

export const createDealClass = (dealId: string, data: {
  name: string;
  min_investment?: number;
  max_investment?: number;
  terms?: Record<string, any>;
}) => post(`${BASE}/deals/${dealId}/classes`, data);

// ============================================================
// Opportunities / Pipeline
// ============================================================
export const getOpportunities = (dealId: string, params?: { stage?: string; view?: string }) =>
  get(`${BASE}/deals/${dealId}/opportunities`, { params });

export const getOpportunity = (id: string) => get(`${BASE}/opportunities/${id}`);

export const createOpportunity = (data: {
  deal_id: string;
  contact_id?: string;
  class_id?: string;
  expected_amount?: number;
  stage?: string;
  description?: string;
}) => post(`${BASE}/opportunities`, data);

export const updateOpportunity = (id: string, data: Partial<{
  stage: string;
  expected_amount: number;
  description: string;
  class_id: string;
  assigned_to: string;
}>) => put(`${BASE}/opportunities/${id}`, data);

// ============================================================
// Commitments
// ============================================================
export const getCommitments = (dealId: string) =>
  get(`${BASE}/deals/${dealId}/commitments`);

export const createCommitment = (data: {
  opportunity_id: string;
  deal_id: string;
  contact_id: string;
  class_id?: string;
  account_name?: string;
  amount: number;
  notes?: string;
}) => post(`${BASE}/commitments`, data);

export const updateCommitment = (id: string, data: Partial<{
  status: string;
  amount: number;
  notes: string;
}>) => put(`${BASE}/commitments/${id}`, data);

// ============================================================
// Capital Calls
// ============================================================
export const getCapitalCalls = () => get(`${BASE}/capital-calls`);

export const createCapitalCall = (data: {
  deal_id: string;
  name: string;
  call_amount: number;
  deadline?: string;
  notes?: string;
}) => post(`${BASE}/capital-calls`, data);

export const updateCapitalCall = (id: string, data: Partial<{
  status: string;
  funded_amount: number;
  notes: string;
}>) => put(`${BASE}/capital-calls/${id}`, data);

// ============================================================
// Contacts
// ============================================================
export const getContacts = (params?: { search?: string; contact_type?: string; page?: number; limit?: number }) =>
  get(`${BASE}/contacts`, { params });

export const getContact = (id: string) => get(`${BASE}/contacts/${id}`);

export const createContact = (data: {
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  company?: string;
  title?: string;
  contact_type?: string;
  source?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  accreditation_status?: string;
  notes?: string;
}) => post(`${BASE}/contacts`, data);

export const updateContact = (id: string, data: Partial<{
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  company: string;
  title: string;
  contact_type: string;
  source: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  accreditation_status: string;
  notes: string;
}>) => put(`${BASE}/contacts/${id}`, data);

export const deleteContact = (id: string): Promise<any> =>
  api.delete(`${BASE}/contacts/${id}`);

export const uploadContactsCSV = (file: File, batchName?: string) => {
  const formData = new FormData();
  formData.append('csv', file);
  if (batchName) formData.append('batch_name', batchName);
  return api.post(`${BASE}/contacts/upload-csv`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export const downloadContactsTemplate = () =>
  get(`${BASE}/contacts/template/csv`, { responseType: 'blob' });

export const uploadPropertiesCSV = (file: File, batchName?: string) => {
  const formData = new FormData();
  formData.append('csv', file);
  if (batchName) formData.append('batch_name', batchName);
  return api.post('/admin/upload-properties-csv', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export const downloadPropertiesTemplate = () =>
  get(`${BASE}/properties/template/csv`, { responseType: 'blob' });

// ============================================================
// Projects
// ============================================================
export const getProjects = () => get(`${BASE}/projects`);
export const getProject = (id: string) => get(`${BASE}/projects/${id}`);

// ============================================================
// Distributions
// ============================================================
export const getDistributions = (params?: { deal_id?: string }) =>
  get(`${BASE}/distributions`, { params });

export const getDistribution = (id: string) => get(`${BASE}/distributions/${id}`);

export const createDistribution = (data: {
  deal_id: string;
  plan_id?: string;
  payment_date: string;
  total_amount: number;
  memo?: string;
  name?: string;
  pay_down_hurdles?: boolean;
}) => post(`${BASE}/distributions`, data);

export const updateDistribution = (id: string, data: { status: string }) =>
  put(`${BASE}/distributions/${id}`, data);

// ============================================================
// Distribution Plans
// ============================================================
export const getDistributionPlans = (params?: { deal_id?: string }) =>
  get(`${BASE}/distribution-plans`, { params });

export const createDistributionPlan = (data: {
  deal_id: string;
  name?: string;
  remainder_allocation?: string;
  enable_reinvestment?: boolean;
  rules?: Array<{ hurdle: string; classes: Record<string, number>; day_count?: string; compounding?: string }>;
}) => post(`${BASE}/distribution-plans`, data);

export const updateDistributionPlan = (id: string, data: any) =>
  put(`${BASE}/distribution-plans/${id}`, data);

// ============================================================
// E-Signature Templates
// ============================================================
export const getEsignTemplates = () => get(`${BASE}/esign-templates`);

export const createEsignTemplate = (data: {
  name: string;
  display_name?: string;
  template_type?: string;
  content_html?: string;
  fields?: any[];
}) => post(`${BASE}/esign-templates`, data);

export const updateEsignTemplate = (id: string, data: any) =>
  put(`${BASE}/esign-templates/${id}`, data);

export const getSignatureRequests = () => get(`${ESIGN_BASE}/signature-requests`);

export const sendForSignature = (data: {
  tenantDocumentId: string;
  signerEmail: string;
  signerName?: string;
  dealId?: string;
  userId?: string;
  riskTier?: number;
  scheduled_at?: string;
}) => post(`${BASE}/send-for-signature`, data);

export const nudgeSignature = (id: string) => post(`${BASE}/signature-requests/${id}/nudge`);

export const getSignatureAuditTrail = (id: string) => get(`${BASE}/signature-requests/${id}/audit-trail`);

export const getDashboardOverview = () => get(`${BASE}/dashboard-overview`);

// ============================================================
// Signature Envelopes
// ============================================================
export const getEnvelopes = (params?: { status?: string; page?: number; limit?: number }) => {
  const qs = new URLSearchParams();
  if (params?.status) qs.set('status', params.status);
  if (params?.page) qs.set('page', String(params.page));
  if (params?.limit) qs.set('limit', String(params.limit));
  const q = qs.toString();
  return get(`${ESIGN_BASE}/envelopes${q ? `?${q}` : ''}`);
};

export const getEnvelope = (id: string) => get(`${BASE}/envelopes/${id}`);

export const createEnvelope = (data: {
  title: string;
  description?: string;
  riskTier?: number;
  routingMode?: string;
  documentIds: string[];
  recipients: { email: string; name?: string; role?: string; signingOrder?: number; documentId?: string; dealId?: string }[];
  expiresAt?: string;
  scheduledAt?: string;
}) => post(`${BASE}/envelopes`, data);

export const voidEnvelope = (id: string, reason: string) => post(`${BASE}/envelopes/${id}/void`, { reason });

export const getEnvelopeAuditTrail = (id: string) => get(`${BASE}/envelopes/${id}/audit-trail`);

export const getEnvelopeCertificate = (id: string) => get(`${BASE}/envelopes/${id}/certificate`);

// ============================================================
// Email Campaigns
// ============================================================
export const getEmailCampaigns = () => get(`${BASE}/email-campaigns`);

export const getEmailCampaign = (id: string) => get(`${BASE}/email-campaigns/${id}`);

export const createEmailCampaign = (data: {
  deal_id?: string;
  name: string;
  subject: string;
  body_html: string;
  from_name?: string;
  from_email?: string;
  pipeline_stage?: string;
  recipient_filter?: Record<string, any>;
}) => post(`${BASE}/email-campaigns`, data);

export const sendEmailCampaign = (id: string, data?: { scheduled_at?: string }) =>
  post(`${BASE}/email-campaigns/${id}/send`, data || {});

// ============================================================
// Action Items
// ============================================================
export const getActionItems = (params?: { completed?: boolean }) =>
  get(`${BASE}/action-items`, { params });

export const createActionItem = (data: {
  deal_id?: string;
  opportunity_id?: string;
  title: string;
  description?: string;
  item_type?: string;
  priority?: number;
  due_date?: string;
  assigned_to?: string;
}) => post(`${BASE}/action-items`, data);

export const updateActionItem = (id: string, data: Partial<{
  completed: boolean;
  title: string;
  description: string;
  priority: number;
  due_date: string;
}>) => put(`${BASE}/action-items/${id}`, data);

// ============================================================
// Engagements (Payment Requests)
// ============================================================
const ENG_BASE = '/admin/engagements';

export const getEngagements = (params?: { status?: string; type?: string; search?: string; page?: number; limit?: number }) =>
  get(`${ENG_BASE}/engagements`, { params });

export const getEngagement = (id: string) => get(`${ENG_BASE}/engagements/${id}`);

export const createEngagement = (data: {
  title: string;
  description?: string;
  contact_id?: string;
  contact_email?: string;
  contact_name?: string;
  type?: string;
  frequency?: string;
  amount_cents: number;
  currency?: string;
  methods_accepted?: string[];
  recurring_count?: number;
  next_due_date?: string;
  deal_id?: string;
  link_expires_at?: string;
}) => post(`${ENG_BASE}/engagements`, data);

export const updateEngagement = (id: string, data: any) =>
  put(`${ENG_BASE}/engagements/${id}`, data);

export const sendEngagementLink = (id: string, data?: { scheduled_at?: string }) =>
  post(`${ENG_BASE}/engagements/${id}/send`, data || {});

export const sendEngagementForSignature = (id: string, data?: { tenantDocumentId?: string; riskTier?: number; scheduled_at?: string }) =>
  post(`${ENG_BASE}/engagements/${id}/send-for-signature`, data || {});

export const nudgeEngagementSignature = (id: string) =>
  post(`${ENG_BASE}/engagements/${id}/nudge-signature`);

export const getEngagementSignatures = (id: string) =>
  get(`${ENG_BASE}/engagements/${id}/signatures`);

// ============================================================
// Admin Transactions (Merchant Report & Reconciliation)
// ============================================================
export const getAdminTransactions = (params?: {
  status?: string; method?: string; type?: string; source?: string; transaction_type?: string; search?: string;
  date_from?: string; date_to?: string;
  has_noc?: string; has_return?: string; reconciled?: string;
  page?: number; limit?: number;
}) => get(`${ENG_BASE}/transactions`, { params });

export const getAdminTransaction = (id: string) =>
  get(`${ENG_BASE}/transactions/${id}`);

export const refundTransaction = (id: string, data: { amount_cents?: number; reason?: string }) =>
  post(`${ENG_BASE}/transactions/${id}/refund`, data);

export const cancelTransaction = (id: string, data?: { reason?: string }) =>
  post(`${ENG_BASE}/transactions/${id}/cancel`, data);

export const reconcileTransaction = (id: string) =>
  post(`${ENG_BASE}/transactions/${id}/reconcile`);

export const reconcileTransactionBatch = (transaction_ids: string[]) =>
  post(`${ENG_BASE}/transactions/reconcile-batch`, { transaction_ids });

// ── Hive Fund wire reconciliation ───────────────────────────────
export const getPendingHiveFundWires = () =>
  get('/hivefund/mgmt/pending-wires');

export const settleHiveFundWire = (investmentId: string, data?: { settledAt?: string; sendEmails?: boolean }) =>
  post(`/hivefund/mgmt/settle-wire/${investmentId}`, data || {});

export const adminSendMoney = (data: {
  recipient_name: string; recipient_email?: string; amount_cents: number;
  description?: string; method: 'ach' | 'usdc'; notes?: string;
  ach_routing?: string; ach_account_number?: string; ach_account_type?: string;
  wallet_address?: string; chain?: string;
}) => post(`${ENG_BASE}/transactions/admin-send`, data);

export const adminDebitAccount = (data: {
  payer_name: string; payer_email?: string; amount_cents: number;
  description?: string; notes?: string;
  ach_routing: string; ach_account_number: string; ach_account_type?: string;
}) => post(`${ENG_BASE}/transactions/admin-debit`, data);

export const getTransactionSummary = (params?: { date_from?: string; date_to?: string }) =>
  get(`${ENG_BASE}/transactions/summary`, { params });

// ============================================================
// Investor Transaction History (my transactions)
// ============================================================
export const getMyTransactions = (params?: { status?: string; method?: string; page?: number; limit?: number }) =>
  get('/my-transactions', { params });

// ============================================================
// Campaign Orchestration — Initiatives
// ============================================================
const CAMP_BASE = '/admin/campaigns';
const del = (url: string): Promise<any> => api.delete(url);

export const getInitiatives = (params?: { status?: string }) =>
  get(`${CAMP_BASE}/initiatives`, { params });

export const getInitiative = (id: string) =>
  get(`${CAMP_BASE}/initiatives/${id}`);

export const createInitiative = (data: { name: string; description?: string; status?: string }) =>
  post(`${CAMP_BASE}/initiatives`, data);

export const updateInitiative = (id: string, data: { name?: string; description?: string; status?: string }) =>
  put(`${CAMP_BASE}/initiatives/${id}`, data);

export const deleteInitiative = (id: string) =>
  del(`${CAMP_BASE}/initiatives/${id}`);

// ============================================================
// Campaign Orchestration — Campaigns
// ============================================================
export const getCampaigns = (params?: { initiative_id?: string; is_active?: string }) =>
  get(`${CAMP_BASE}/campaigns`, { params });

export const getCampaign = (id: string) =>
  get(`${CAMP_BASE}/campaigns/${id}`);

export const createCampaign = (data: {
  initiative_id: string;
  name: string;
  description?: string;
  trigger_event: string;
  enrollment_condition?: Record<string, any>;
  exit_condition?: Record<string, any>;
  is_active?: boolean;
}) => post(`${CAMP_BASE}/campaigns`, data);

export const updateCampaign = (id: string, data: Partial<{
  name: string;
  description: string;
  trigger_event: string;
  enrollment_condition: Record<string, any>;
  exit_condition: Record<string, any>;
  is_active: boolean;
}>) => put(`${CAMP_BASE}/campaigns/${id}`, data);

// ============================================================
// Campaign Orchestration — Steps
// ============================================================
export const createCampaignStep = (campaignId: string, data: {
  step_number: number;
  delay_hours?: number;
  action_type?: string;
  subject: string;
  body_html?: string;
  body_text?: string;
  template_id?: string;
  condition_json?: Record<string, any>;
}) => post(`${CAMP_BASE}/campaigns/${campaignId}/steps`, data);

export const updateCampaignStep = (campaignId: string, stepId: string, data: Partial<{
  delay_hours: number;
  subject: string;
  body_html: string;
  body_text: string;
  template_id: string;
  condition_json: Record<string, any>;
  is_active: boolean;
}>) => put(`${CAMP_BASE}/campaigns/${campaignId}/steps/${stepId}`, data);

export const deleteCampaignStep = (campaignId: string, stepId: string) =>
  del(`${CAMP_BASE}/campaigns/${campaignId}/steps/${stepId}`);

// ============================================================
// Campaign Orchestration — Enrollments
// ============================================================
export const enrollContact = (data: { campaign_id: string; contact_id: string }) =>
  post(`${CAMP_BASE}/enrollments`, data);

export const bulkEnrollContacts = (data: { campaign_id: string; contact_ids: string[] }) =>
  post(`${CAMP_BASE}/enrollments/bulk`, data);

export const exitEnrollment = (enrollmentId: string, reason?: string) =>
  put(`${CAMP_BASE}/enrollments/${enrollmentId}/exit`, { reason });

export const getContactEnrollments = (contactId: string) =>
  get(`${CAMP_BASE}/contacts/${contactId}/enrollments`);

// ============================================================
// Campaign Orchestration — Invites & Lifecycle
// ============================================================
export const sendContactInvite = (contactId: string) =>
  post(`${CAMP_BASE}/contacts/${contactId}/invite`);

export const getContactInvites = (contactId: string) =>
  get(`${CAMP_BASE}/contacts/${contactId}/invites`);

export const updateContactLifecycle = (contactId: string, stage: string) =>
  put(`${CAMP_BASE}/contacts/${contactId}/lifecycle`, { stage });

// ============================================================
// Campaign Orchestration — Events & Stats
// ============================================================
export const getCampaignEvents = (params?: { contact_id?: string; campaign_id?: string; event_type?: string; limit?: number }) =>
  get(`${CAMP_BASE}/campaign-events`, { params });

export const getOrchestrationStats = () =>
  get(`${CAMP_BASE}/orchestration/stats`);

// ============================================================
// KPI Dashboard
// ============================================================
export const getKpiFunnel = () =>
  get(`${CAMP_BASE}/kpi/funnel`);

export const getKpiVelocity = () =>
  get(`${CAMP_BASE}/kpi/velocity`);

export const getKpiRisk = () =>
  get(`${CAMP_BASE}/kpi/risk`);

export const getKpiCampaignPerformance = () =>
  get(`${CAMP_BASE}/kpi/campaign-performance`);

export const getKpiReferrals = () =>
  get(`${CAMP_BASE}/kpi/referrals`);

export const getKpiDashboard = () =>
  get(`${CAMP_BASE}/kpi/dashboard`);

// ============================================================
// Referrals
// ============================================================
export const getReferrals = () =>
  get(`${CAMP_BASE}/referrals`);

export const createReferral = (data: { referrer_user_id: string; referred_contact_id: string }) =>
  post(`${CAMP_BASE}/referrals`, data);

export const updateReferralStatus = (id: string, data: { status?: string; reward_status?: string }) =>
  put(`${CAMP_BASE}/referrals/${id}/status`, data);

export const getReferralLeaderboard = () =>
  get(`${CAMP_BASE}/referrals/leaderboard`);

// ============================================================
// Vendors
// ============================================================
const VENDOR_BASE = '/admin/vendors';

export const getVendors = (params?: { status?: string; search?: string; page?: number; limit?: number }) =>
  get(`${VENDOR_BASE}/vendors`, { params });

export const getVendorSummary = () =>
  get(`${VENDOR_BASE}/vendors/summary`);

export const getVendor = (id: string) =>
  get(`${VENDOR_BASE}/vendors/${id}`);

export const createVendor = (data: {
  business_name: string;
  contact_name?: string;
  email?: string;
  phone?: string;
  tax_id?: string;
  tax_type?: 'ein' | 'ssn';
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  bank_name?: string;
  ach_routing?: string;
  ach_account_number?: string;
  ach_account_type?: 'checking' | 'savings';
  payment_terms?: string;
  default_gl_code?: string;
  notes?: string;
}) => post(`${VENDOR_BASE}/vendors`, data);

export const updateVendor = (id: string, data: any) =>
  put(`${VENDOR_BASE}/vendors/${id}`, data);

// Vendor Invoices
export const getInvoices = (params?: {
  vendor_id?: string;
  status?: string;
  search?: string;
  overdue?: string;
  page?: number;
  limit?: number;
}) => get(`${VENDOR_BASE}/invoices`, { params });

export const getInvoice = (id: string) =>
  get(`${VENDOR_BASE}/invoices/${id}`);

export const createInvoice = (data: {
  vendor_id: string;
  invoice_number?: string;
  description?: string;
  amount_cents: number;
  gl_code?: string;
  line_items?: Array<{ description: string; quantity: number; unit_price_cents: number; amount_cents: number }>;
  invoice_date?: string;
  due_date?: string;
  attachments?: Array<{ name: string; url: string }>;
}) => post(`${VENDOR_BASE}/invoices`, data);

export const updateInvoice = (id: string, data: any) =>
  put(`${VENDOR_BASE}/invoices/${id}`, data);

export const approveInvoice = (id: string) =>
  post(`${VENDOR_BASE}/invoices/${id}/approve`);

export const payInvoice = (id: string) =>
  post(`${VENDOR_BASE}/invoices/${id}/pay`);

export const voidInvoice = (id: string) =>
  post(`${VENDOR_BASE}/invoices/${id}/void`);

// ============================================================
// Payroll
// ============================================================
const PAYROLL_BASE = '/admin/payroll';

export const getTeamMembers = (params?: {
  status?: string;
  employment_type?: string;
  search?: string;
  page?: number;
  limit?: number;
}) => get(`${PAYROLL_BASE}/team`, { params });

export const getTeamSummary = () =>
  get(`${PAYROLL_BASE}/team/summary`);

export const getTeamMember = (id: string) =>
  get(`${PAYROLL_BASE}/team/${id}`);

export const createTeamMember = (data: {
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  ssn?: string;
  date_of_birth?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  zip?: string;
  employment_type?: 'w2' | '1099';
  job_title?: string;
  department?: string;
  hire_date?: string;
  pay_rate_cents: number;
  pay_frequency?: 'weekly' | 'biweekly' | 'semi_monthly' | 'monthly';
  annual_salary_cents?: number;
  bank_name?: string;
  ach_routing?: string;
  ach_account_number?: string;
  ach_account_type?: 'checking' | 'savings';
  documents?: Array<{ type: string; name: string; url: string }>;
}) => post(`${PAYROLL_BASE}/team`, data);

export const updateTeamMember = (id: string, data: any) =>
  put(`${PAYROLL_BASE}/team/${id}`, data);

// Payroll Runs
export const getPayrollRuns = (params?: { status?: string; page?: number; limit?: number }) =>
  get(`${PAYROLL_BASE}/runs`, { params });

export const getPayrollRun = (id: string) =>
  get(`${PAYROLL_BASE}/runs/${id}`);

export const createPayrollRun = (data: {
  pay_period_start: string;
  pay_period_end: string;
  pay_date: string;
  notes?: string;
  member_ids?: string[];
}) => post(`${PAYROLL_BASE}/runs`, data);

export const updatePayrollItem = (runId: string, itemId: string, data: {
  gross_pay_cents?: number;
  deductions_cents?: number;
  deductions?: Record<string, number>;
  regular_hours?: number;
  overtime_hours?: number;
}) => put(`${PAYROLL_BASE}/runs/${runId}/items/${itemId}`, data);

export const approvePayrollRun = (id: string) =>
  post(`${PAYROLL_BASE}/runs/${id}/approve`);

export const processPayrollRun = (id: string) =>
  post(`${PAYROLL_BASE}/runs/${id}/process`);

export const cancelPayrollRun = (id: string) =>
  post(`${PAYROLL_BASE}/runs/${id}/cancel`);

